// ResetPassword.tsx - Reset password form component
import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Button,
  Text,
  Title,
  Alert,
  PasswordInput,
  Group,
  Center,
  Progress,
} from '@mantine/core';
import {
  IconLock,
  IconCheck,
  IconAlertCircle,
  IconArrowLeft,
  IconKey,
} from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset token. Please request a new password reset link.');
    }
  }, [token]);

  const getPasswordStrength = (pwd: string): number => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (pwd.length >= 12) strength += 25;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 25;
    if (/\d/.test(pwd)) strength += 12.5;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 12.5;
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);
  const getStrengthColor = () => {
    if (passwordStrength < 50) return 'red';
    if (passwordStrength < 75) return 'yellow';
    return 'green';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!token) {
      setError('Invalid reset token');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/auth/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, password }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
          <Stack align="center" gap="md">
            <IconCheck size={64} color="green" />
            <Title order={2} ta="center">
              Password Reset Successful
            </Title>
            <Text ta="center" c="dimmed">
              Your password has been reset successfully. You can now log in with your new password.
            </Text>
            <Button
              fullWidth
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
        <Stack gap="md">
          <div>
            <Title order={2} mb="xs">
              Reset Your Password
            </Title>
            <Text c="dimmed" size="sm">
              Enter your new password below.
            </Text>
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <PasswordInput
                label="New Password"
                placeholder="Enter new password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftSection={<IconLock size={16} />}
                disabled={loading || !token}
              />

              {password && (
                <div>
                  <Progress
                    value={passwordStrength}
                    color={getStrengthColor()}
                    size="sm"
                    mb="xs"
                  />
                  <Text size="xs" c="dimmed">
                    Password strength: {passwordStrength < 50 ? 'Weak' : passwordStrength < 75 ? 'Medium' : 'Strong'}
                  </Text>
                </div>
              )}

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm new password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                leftSection={<IconKey size={16} />}
                disabled={loading || !token}
                error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : null}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!token}
                leftSection={<IconKey size={16} />}
              >
                Reset Password
              </Button>

              <Button
                variant="subtle"
                fullWidth
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Center>
  );
}
